// "Mijn e-mailinstellingen" (v18.4 round 3, 3.3) — iedere gebruiker regelt zijn eigen mail.
import React, { useEffect, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { getAccessToken } from '@/lib/tokenStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v2/email`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
};

export const CATEGORIE_LABELS = {
  checklist_nieuw: 'Nieuwe checklist klaar',
  checklist_verlopen: 'Verlopen en escalaties',
  checklist_ingevuld: 'Checklist ingevuld',
  checklist_herinnering: 'Herinneringen',
  dagoverzicht: 'Dagoverzicht checklists',
  weekoverzicht: 'Weekoverzicht checklists',
  performance: 'Performance digests',
};

export const EmailVoorkeuren = ({ waarde, onWijzig, testPrefix = 'voorkeur',
  geblokkeerd = false }) => (
  <div className="space-y-2" data-testid={`${testPrefix}-lijst`}>
    <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
      <span className="text-sm font-medium text-slate-700">Alle e-mails uit</span>
      <Switch checked={!!waarde.alle_uit} data-testid={`${testPrefix}-alle-uit`}
        aria-label="Alle e-mails uit" disabled={geblokkeerd}
        onCheckedChange={v => onWijzig({ ...waarde, alle_uit: v })} />
    </div>
    {Object.entries(CATEGORIE_LABELS).map(([sleutel, label]) => (
      <div key={sleutel} className="flex items-center justify-between px-3 py-1.5">
        <span className={`text-sm ${waarde.alle_uit ? 'text-slate-300' : 'text-slate-600'}`}>{label}</span>
        <Switch checked={waarde[sleutel] !== false}
          disabled={!!waarde.alle_uit || geblokkeerd}
          aria-label={label} data-testid={`${testPrefix}-${sleutel}`}
          onCheckedChange={v => onWijzig({ ...waarde, [sleutel]: v })} />
      </div>
    ))}
  </div>
);

export default function MijnEmailInstellingen() {
  const [voorkeuren, setVoorkeuren] = useState(null);
  const [laadFout, setLaadFout] = useState(false);
  const [bezig, setBezig] = useState(false);

  // 6.22: een mislukte GET mag nooit als "alles aan" gelden — dan overschrijft
  // één klik de echte instellingen.
  useEffect(() => {
    fetch(`${API}/voorkeuren/mij`, { headers: hdrs() })
      .then(r => { if (!r.ok) throw new Error('laden mislukt'); return r.json(); })
      .then(d => setVoorkeuren(d || {}))
      .catch(() => { setLaadFout(true); setVoorkeuren({}); });
  }, []);

  const bewaar = async (nieuw) => {
    const vorige = voorkeuren;
    setVoorkeuren(nieuw);
    setBezig(true);
    try {
      const res = await fetch(`${API}/voorkeuren/mij`, {
        method: 'PUT', headers: hdrs(), body: JSON.stringify(nieuw),
      });
      if (!res.ok) throw new Error('opslaan mislukt');
      toast.success('Opgeslagen');
    } catch {
      setVoorkeuren(vorige);   // 6.22: terugdraaien, niet stil laten staan
      toast.error('Opslaan mislukt — je instelling is niet gewijzigd.');
    } finally { setBezig(false); }
  };

  if (!voorkeuren) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#1f2548]" /></div>;
  }

  if (laadFout) {
    return (
      <div className="max-w-xl mx-auto p-6" data-testid="voorkeuren-laadfout">
        <p className="text-sm text-red-600">E-mailinstellingen konden niet worden geladen.</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-0 space-y-5" data-testid="mijn-email-instellingen">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#1f2548] flex items-center gap-2">
          <Mail className="w-6 h-6" /> Mijn e-mailinstellingen
        </h1>
        <p className="text-sm text-slate-500">Bepaal zelf welke mail je van de Hub ontvangt</p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <EmailVoorkeuren waarde={voorkeuren} onWijzig={bewaar} testPrefix="mijn-voorkeur"
          geblokkeerd={laadFout || bezig} />
        {bezig && <p className="text-xs text-slate-400 mt-2">Opslaan...</p>}
      </div>
    </div>
  );
}
